import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { withTheme } from 'common/styling/theme';
import DefaultButton from 'common/components/DefaultButton/DefaultButton';
import { RadioButton, BasicCRMModal } from 'crm/_components';
import { FormMessageHOC } from 'common/HOCs/FormMessageHOC';
import { uploadContacts, clearUploadContactsResult } from 'crm/contacts/_redux';
import { compose } from 'redux';
import CreateContactForm from '../CreateContactForm';
import UploadContactForm from '../UploadContactForm';

import { staticStyles, getDynamicStyles } from './style';

const AddContact = ({ isOpen, onRequestClose, theme, showFormSuccessMessage }) => {
  const dynamicStyles = Object.keys(theme).length ? getDynamicStyles(theme) : ` `;
  const dispatch = useDispatch();
  const [activeForm, setActiveForm] = useState('create');
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [conflictEmails, setConflictEmails] = useState([]);
  const [conflictPhones, setConflictPhones] = useState([]);
  const [isUploading, setIsUploading] = useState(false); // 新增状态，跟踪是否有上传请求正在进行

  // 使用 useSelector 监听上传结果
  const uploadResult = useSelector(state => state.crm.contacts.uploadContactsResult);
  const uploadLoading = useSelector(state => state.crm.contacts.uploadContactsLoading); // 监听上传请求的加载状态

  // 监听上传结果变化
  useEffect(() => {
    if (uploadResult && isUploading && !uploadLoading) {
      // 检查响应中是否有冲突信息
      if (uploadResult.conflict_emails?.length > 0 || uploadResult.conflict_phones?.length > 0) {
        setConflictEmails(uploadResult.conflict_emails || []);
        setConflictPhones(uploadResult.conflict_phones || []);
        setShowErrorModal(true);
        // 不关闭当前弹窗
      } else {
        // 上传成功，显示成功提示
        showFormSuccessMessage('operationSuccessful');
        onRequestClose();
      }
      setIsUploading(false); // 无论成功失败，都重置上传状态
    }
  }, [uploadResult, uploadLoading, onRequestClose, showFormSuccessMessage, isUploading]);

  const handleUploadContacts = uploadFile => {
    setIsUploading(true); // 设置上传状态为true
    dispatch(uploadContacts.request(uploadFile));
    // 点击上传按钮后隐藏当前弹框
    onRequestClose();
  };

  // 修改handleErrorModalClose函数
  const handleErrorModalClose = () => {
    setShowErrorModal(false);
    setConflictEmails([]);
    setConflictPhones([]);
    // 清除Redux中的上传结果
    dispatch(clearUploadContactsResult());
  };

  // 添加在isOpen状态变化时清除之前的上传结果
  useEffect(() => {
    if (isOpen) {
      dispatch(clearUploadContactsResult());
      setIsUploading(false); // 确保打开弹窗时重置上传状态
    }
  }, [isOpen, dispatch]);

  return (
    <>
      <BasicCRMModal captionId="crmUploadContact" isOpen={isOpen} onRequestClose={onRequestClose}>
        <div className="AddContact">
          <div className="AddContact__controls">
            <RadioButton
              textId="crmCreateContact"
              active={activeForm === 'create'}
              onClick={() => setActiveForm('create')}
            />
            <RadioButton
              textId="crmUploadContact"
              active={activeForm === 'upload'}
              onClick={() => {
                setActiveForm('upload');
                setShowErrorModal(false);
              }}
            />
          </div>
          {activeForm === 'create' ? (
            <CreateContactForm onSuccess={onRequestClose} />
          ) : (
            <UploadContactForm onSubmit={handleUploadContacts} />
          )}
          <style jsx>{staticStyles}</style>
          <style>{dynamicStyles}</style>
        </div>
      </BasicCRMModal>

      {/* 导入失败弹窗 */}
      {showErrorModal && (
        <BasicCRMModal
          isOpen={showErrorModal}
          onRequestClose={handleErrorModalClose}
          caption="Import Failed!"
          alignTitleCenter>
          <div className="BasicCRMModal__error-content" style={{ maxWidth: '500px' }}>
            <p style={{ textAlign: 'left', marginBottom: '16px' }}>
              Some phone numbers or email addresses already exist in the system. Please update them and try importing
              again.Unique data has been imported.
            </p>
            <p style={{ textAlign: 'left', marginBottom: '16px' }}>Conflicting entries：</p>
            {conflictPhones.length > 0 && (
              <div className="BasicCRMModal__error-section">
                <p className="BasicCRMModal__error-title">·Phone Numbers: {conflictPhones.join(', ')}</p>
              </div>
            )}
            {conflictEmails.length > 0 && (
              <div className="BasicCRMModal__error-section">
                <p className="BasicCRMModal__error-title">·Email Addresses: {conflictEmails.join(', ')}</p>
              </div>
            )}

            <div style={{ marginTop: '20px', textAlign: 'center' }}>
              <DefaultButton
                textId="justClose"
                onClick={handleErrorModalClose}
                style={{ backgroundColor: '#0AA1E2', borderColor: '#0AA1E2', color: '#FFF' }}
              />
            </div>
          </div>
        </BasicCRMModal>
      )}
    </>
  );
};

AddContact.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onRequestClose: PropTypes.func.isRequired,
  theme: PropTypes.object,
  showFormSuccessMessage: PropTypes.func.isRequired,
};

AddContact.defaultProps = {
  theme: {},
};

export default compose(withTheme(), FormMessageHOC)(AddContact);
export { AddContact };
